import { formatDistance } from 'date-fns';

const UTC_OR_OFFSET_REGEX = /(Z|[+-]\d{2}:\d{2}|[+-]\d{4})$/i;

/**
 * Map common timezone abbreviations to IANA timezone names.
 * Intl.DateTimeFormat requires IANA names (e.g., "America/New_York"), not abbreviations (e.g., "EST").
 */
const TIMEZONE_ABBREVIATION_MAP: Record<string, string> = {
  // US timezones
  EST: 'America/New_York',
  EDT: 'America/New_York',
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  MST: 'America/Denver',
  MDT: 'America/Denver',
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  AKST: 'America/Anchorage',
  AKDT: 'America/Anchorage',
  HST: 'Pacific/Honolulu',
  // Common international
  UTC: 'UTC',
  GMT: 'UTC',
  IST: 'Asia/Kolkata',
  BST: 'Europe/London',
  CET: 'Europe/Paris',
  CEST: 'Europe/Paris',
  AEST: 'Australia/Sydney',
  AEDT: 'Australia/Sydney',
};

/**
 * Normalize timezone: convert abbreviations to IANA names, pass through valid IANA names.
 * If the input is already an IANA name (contains '/'), it's returned as-is.
 */
const normalizeTimeZone = (tz: string): string => {
  const trimmed = tz.trim();
  
  // Already an IANA name (e.g., "America/New_York")
  if (trimmed.includes('/')) {
    return trimmed;
  }
  
  const upper = trimmed.toUpperCase();
  const mapped = TIMEZONE_ABBREVIATION_MAP[upper];
  
  if (mapped) {
    return mapped;
  }
  
  // Warn in development if we encounter an unknown abbreviation
  if (process.env.NODE_ENV === 'development' && !trimmed.includes('/')) {
    console.warn(
      `[dateTimeWithPracticeZone] Unknown timezone abbreviation: "${tz}". ` +
      `Add it to TIMEZONE_ABBREVIATION_MAP or use an IANA name like "America/New_York".`
    );
  }
  
  return trimmed;
};

const shouldApplyPracticeTimezone = (value: string | Date, timeZone?: string): boolean => {
  if (!timeZone) return false;
  if (value instanceof Date) return true;
  return UTC_OR_OFFSET_REGEX.test(value.trim());
};

const parseDateInput = (value: string | Date): Date | null => {
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Naive local datetime: no trailing Z or ±offset (API means practice wall clock). */
const NAIVE_LOCAL_DATETIME_REGEX =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/;

/**
 * Interpret a naive ISO datetime as wall-clock time in `timeZone`, return the UTC instant.
 * (e.g. "2026-04-30T05:42:20" + America/New_York → instant that is 5:42 AM Eastern on that date.)
 */
export const parseNaiveIsoAsTimeZoneWallClockToUtc = (
  naiveIso: string,
  timeZone: string,
): Date | null => {
  const m = naiveIso.trim().match(NAIVE_LOCAL_DATETIME_REGEX);
  if (!m) return null;

  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const h = Number(m[4]);
  const mi = Number(m[5]);
  const s = Number(m[6]);

  const tz = normalizeTimeZone(timeZone);

  // Step 1: Create UTC guess
  const utcGuess = new Date(Date.UTC(y, mo - 1, d, h, mi, s));

  // Step 2: Get what that instant looks like in target TZ
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(utcGuess);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);

  const tzYear = get('year');
  const tzMonth = get('month');
  const tzDay = get('day');
  const tzHour = get('hour');
  const tzMinute = get('minute');
  const tzSecond = get('second');

  // Step 3: Calculate offset difference
  const diffMs =
    Date.UTC(y, mo - 1, d, h, mi, s) -
    Date.UTC(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute, tzSecond);

  // Step 4: Adjust UTC
  return new Date(utcGuess.getTime() + diffMs);
};

/**
 * Format a time for the **viewer's local timezone** (browser).
 * - Zoned strings (Z or ±offset): parsed as an instant → local clock.
 * - Naive `YYYY-MM-DDTHH:mm:ss` + practiceTimeZone: wall clock in that zone → local clock.
 * - Otherwise: `Date` parsing fallback.
 */
export const formatTimeInUserLocale = (
  value: string | Date,
  practiceTimeZone: string | undefined,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-US',
): string => {
  if (value instanceof Date) {
    return value.toLocaleTimeString(locale, options);
  }
  const str = value.trim();
  const parsed = parseDateInput(str);
  if (!parsed) return str;

  const { timeZone: _omit, ...rest } = options;

  if (UTC_OR_OFFSET_REGEX.test(str)) {
    return parsed.toLocaleTimeString(locale, rest);
  }

  if (practiceTimeZone) {
    const instant = parseNaiveIsoAsTimeZoneWallClockToUtc(str, practiceTimeZone);
    if (instant) {
      return instant.toLocaleTimeString(locale, rest);
    }
  }

  return parsed.toLocaleTimeString(locale, rest);
};

/**
 * Format a calendar date for the **viewer's local timezone** (browser).
 * Same naive / zoned rules as {@link formatTimeInUserLocale}.
 */
export const formatDateInUserLocale = (
  value: string | Date,
  practiceTimeZone: string | undefined,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-US',
): string => {
  if (value instanceof Date) {
    return value.toLocaleDateString(locale, options);
  }
  const str = value.trim();
  const parsed = parseDateInput(str);
  if (!parsed) return str;

  const { timeZone: _omit, ...rest } = options;

  if (UTC_OR_OFFSET_REGEX.test(str)) {
    return parsed.toLocaleDateString(locale, rest);
  }

  if (practiceTimeZone) {
    const instant = parseNaiveIsoAsTimeZoneWallClockToUtc(str, practiceTimeZone);
    if (instant) {
      return instant.toLocaleDateString(locale, rest);
    }
  }

  return parsed.toLocaleDateString(locale, rest);
};

const toWallClockDate = (date: Date, timeZone: string): Date => {
  const tz = normalizeTimeZone(timeZone);
  
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date);

    const getPart = (type: Intl.DateTimeFormatPartTypes) => {
      const part = parts.find((p) => p.type === type)?.value;
      return part ? Number(part) : NaN;
    };

    const year = getPart('year');
    const month = getPart('month');
    const day = getPart('day');
    const hour = getPart('hour');
    const minute = getPart('minute');
    const second = getPart('second');

    if ([year, month, day, hour, minute, second].some(Number.isNaN)) {
      return date;
    }

    return new Date(year, month - 1, day, hour, minute, second);
  } catch (error) {
    // Invalid timezone — fall back to original date
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[dateTimeWithPracticeZone] Invalid timezone: "${tz}"`, error);
    }
    return date;
  }
};

export const getComparableDateWithPracticeTimeZone = (
  value: string | Date,
  timeZone?: string
): Date | null => {
  const parsed = parseDateInput(value);
  if (!parsed) return null;

  if (!shouldApplyPracticeTimezone(value, timeZone) || !timeZone) {
    return parsed;
  }

  return toWallClockDate(parsed, timeZone);
};

export const formatDateWithPracticeTimeZone = (
  value: string | Date,
  timeZone: string | undefined,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-US'
): string => {
  const parsed = parseDateInput(value);
  if (!parsed) return typeof value === 'string' ? value : '';

  if (shouldApplyPracticeTimezone(value, timeZone) && timeZone) {
    const tz = normalizeTimeZone(timeZone);
    return parsed.toLocaleDateString(locale, { ...options, timeZone: tz });
  }

  return parsed.toLocaleDateString(locale, options);
};

export const formatTimeWithPracticeTimeZone = (
  value: string | Date,
  timeZone: string | undefined,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-US'
): string => {
  const parsed = parseDateInput(value);
  if (!parsed) return typeof value === 'string' ? value : '';

  if (shouldApplyPracticeTimezone(value, timeZone) && timeZone) {
    const tz = normalizeTimeZone(timeZone);
    return parsed.toLocaleTimeString(locale, { ...options, timeZone: tz });
  }

  return parsed.toLocaleTimeString(locale, options);
};

export const formatDateTimeWithPracticeTimeZone = (
  value: string | Date,
  timeZone: string | undefined,
  options?: Intl.DateTimeFormatOptions,
  locale?: string
): string => {
  const parsed = parseDateInput(value);
  if (!parsed) return typeof value === 'string' ? value : '';

  const resolvedLocale = locale;
  const resolvedOptions = options ?? {};

  if (shouldApplyPracticeTimezone(value, timeZone) && timeZone) {
    const tz = normalizeTimeZone(timeZone);
    return parsed.toLocaleString(resolvedLocale, { ...resolvedOptions, timeZone: tz });
  }

  return parsed.toLocaleString(resolvedLocale, resolvedOptions);
};

export const formatRelativeToNowWithPracticeTimeZone = (
  value: string | Date,
  timeZone?: string
): string => {
  const isNaiveString =
    typeof value === 'string' &&
    !UTC_OR_OFFSET_REGEX.test(value.trim());

  let target: Date | null;
  let now: Date;

  if (isNaiveString && timeZone) {
    // Naive string like "2026-04-08T07:09:56" — the numbers ARE the practice-local
    // wall-clock time. new Date() parses them as browser-local, but that's fine
    // because we also convert "now" to practice wall-clock using the same
    // browser-local Date constructor. Both are in the same reference frame.
    const tz = normalizeTimeZone(timeZone);
    target = parseDateInput(value);
    now = toWallClockDate(new Date(), tz);
  } else if (timeZone) {
    // Zoned string (has Z/offset) or Date object — convert both to practice wall-clock.
    const tz = normalizeTimeZone(timeZone);
    const parsed = parseDateInput(value);
    if (!parsed) return '';
    target = toWallClockDate(parsed, tz);
    now = toWallClockDate(new Date(), tz);
  } else {
    // No timezone info — fall back to browser-local comparison
    target = parseDateInput(value);
    now = new Date();
  }

  if (!target) return '';
  return formatDistance(target, now, { addSuffix: true });
};
